import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _TablePaginationFoote;
const _excluded = ["sx", "disableGutters", "maxWidth", "fixed", "header", "toolbarItems", "tablePreContent", "filterComponent"];
import PropTypes from 'prop-types';
import * as React from 'react';
import { Container, Paper, extendSX } from '@lumahealthhq/ui-core';
import { Table, TablePaginationFooter, TableToolbar } from './components';
import TableProvider from './contexts/TableProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 *
 * Demos:
 *
 * - [ContainerTable](https://ui-packages.isbuildingluma.com/ui-components/react-container-table/)
 *
 * API:
 *
 * - [ContainerTable API](https://ui-packages.isbuildingluma.com/ui-components/api/container-table/)
 * - inherits [Container API](https://ui-packages.isbuildingluma.com/ui-core/api/container/)
 */
const ContainerTable = /*#__PURE__*/React.forwardRef(function ContainerTable(_ref, ref) {
  let {
      sx,
      disableGutters = true,
      maxWidth = false,
      fixed,
      header,
      toolbarItems,
      tablePreContent,
      filterComponent
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(TableProvider, _extends({}, props, {
    children: /*#__PURE__*/_jsxs(Container, {
      ref: ref,
      sx: extendSX(sx, {
        display: 'grid',
        height: '100%'
      }),
      maxWidth: maxWidth,
      disableGutters: disableGutters,
      fixed: fixed,
      children: [header, /*#__PURE__*/_jsxs(Paper, {
        sx: {
          overflow: 'auto',
          maxWidth: '100%',
          maxHeight: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          px: 1
        },
        children: [/*#__PURE__*/_jsx(TableToolbar, {
          filterComponent: filterComponent,
          renderLabel: props.renderLabel,
          groupMenuOptions: props.groupMenuOptions,
          withSearch: props.withSearch,
          children: toolbarItems && toolbarItems
        }), tablePreContent, /*#__PURE__*/_jsx(Table, _extends({}, props)), _TablePaginationFoote || (_TablePaginationFoote = /*#__PURE__*/_jsx(TablePaginationFooter, {}))]
      })]
    })
  }));
});
process.env.NODE_ENV !== "production" ? ContainerTable.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit "ContainerTableProps.ts" and run "yarn proptypes"
  // ----------------------------------------------------------------------
  /**
   * Optional base API path override. If not provided, defaults to the pluralized model name.
   * Example: For model 'patient', default baseApi would be 'patients',
   * but you could override it to 'patient' or 'medical-records'
   */
  baseApi: PropTypes.string,
  /**
   * The columns of the table.
   */
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  /**
   * If `true`, the left and right padding is removed.
   * @default true
   */
  disableGutters: PropTypes.bool,
  /**
   * Custom element to show when no items are found.
   */
  emptyContent: PropTypes.node,
  /**
   * Filter to render inside toolbar.
   */
  filterComponent: PropTypes.node,
  /**
   * Set the max-width to match the min-width of the current breakpoint.
   * This is useful if you'd prefer to design for a fixed set of sizes
   * instead of trying to accommodate a fully fluid viewport.
   * It's fluid by default.
   * @default false
   */
  fixed: PropTypes.bool,
  /**
   * Bulk actions.
   * the label of the groupMenuOptions is "Actions" by default, if you want to, you can set
   * a different one.
   */
  groupMenuOptions: PropTypes.arrayOf(PropTypes.object),
  /**
   * Custom header component.
   */
  header: PropTypes.node,
  /**
   * Container Table already handle the loading statement, if you pass your isLoading, you will be forcing the Table to
   * show the loading state even if its not loading the data
   */
  isLoading: PropTypes.bool,
  /**
   * TODO -> use useIsMutating with the Model so the dev dont need to pass it down
   * If is mutating any data of the table.
   */
  isMutating: PropTypes.bool,
  /**
   * Determine the max-width of the container.
   * The container width grows with the size of the screen.
   * Set to `false` to disable `maxWidth`.
   * @default false
   */
  maxWidth: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs', false]),
  /**
   * Model to use.
   */
  model: PropTypes.oneOf(['actionQueueItem', 'activity', 'all', 'apiClient', 'appointment', 'appointmentType', 'assistant', 'assistantAction', 'assistantInstance', 'availability', 'billingCopay', 'billingTransaction', 'broadcast', 'broadcastStream', 'broadcastTarget', 'campaign', 'campaignStream', 'campaignTarget', 'ccdaCodeSystem', 'ccdaValueSet', 'ccdaValueSetData', 'chatActivity', 'chatActivityReason', 'chatAudit', 'checklist', 'checklistTemplate', 'conditionsSet', 'customWebStyle', 'diagnosis', 'estimate', 'facility', 'faxDocument', 'faxDocumentActionInstance', 'faxDocumentCategory', 'faxDocumentWorkflow', 'feedbackResponse', 'fileMapping', 'fileUpload', 'followup', 'followupWorkflow', 'followupWorkflowGallery', 'followupWorkflowInstance', 'group', 'guest', 'hl7message', 'inboundMessageRequest', 'insurance', 'insuranceEligibility', 'insurancePayor', 'insuranceVerification', 'insuranceVerificationScheduler', 'integrator', 'integratorClient', 'intent', 'largeLanguageModelPrompt', 'largeLanguageModelRequest', 'login', 'lumaBotFlow', 'lumaBotFlowTemplate', 'message', 'messageFailure', 'messageRegistration', 'messageRequest', 'messageTemplate', 'messageTemplatePartial', 'nlpIntent', 'nlpIntentEntity', 'none', 'offer', 'organization', 'outboundNumber', 'outboundReferral', 'page', 'patient', 'patientCreditCard', 'patientForm', 'patientFormTemplate', 'patientMessageTemplate', 'paymentAudit', 'procedure', 'provider', 'queuemanagerinstance', 'queuemanagertemplate', 'recall', 'referral', 'reminder', 'schedule', 'setting', 'specialty', 'sync', 'telehealth', 'upload', 'user', 'utterance', 'waitingRoom', 'waitingRoomPatient', 'waitlist']).isRequired,
  /**
   * @deprecated avoid onRowClick. Prefer rowProps.
   * E.g: rowProps={{
   *       onClick: (_, item) => {
   *         alert(`Patient ${item.patient?.name} clicked`);
   *       },
   *     }}
   */
  onRowClick: PropTypes.func,
  /**
   * Pagination config.
   */
  paginationOptions: PropTypes.shape({
    initialRowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number)
  }),
  /**
   * Additional params we want to pass to the the API calls.
   *
   * @default {}
   */
  params: PropTypes.object,
  /**
   * the post fix for the model, E.g: model: 'patient' postFix: 'search' ==> will fetch /v2/patient/search.
   */
  postFix: PropTypes.arrayOf(PropTypes.string),
  /**
   * Props passed down to query to fetch paginated results.
   */
  queryOptions: PropTypes.shape({
    cacheTime: PropTypes.number,
    enabled: PropTypes.bool,
    onError: PropTypes.func,
    onSettled: PropTypes.func,
    onSuccess: PropTypes.func,
    placeholderData: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
      })).isRequired,
      totalSize: PropTypes.number.isRequired
    }),
    refetchInterval: PropTypes.number,
    refetchOnWindowsFocus: PropTypes.bool,
    retry: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.bool]),
    staleTime: PropTypes.number
  }),
  /**
   * Function that returns a string or a custom component.
   * If the given function returns a custom component, it will not shows the selectedNumbers by default,
   * only the Close Icon.
   * If returns a string, it will be styled and the selectedNumber will be displayed.
   */
  renderLabel: PropTypes.func,
  /**
   * Props for customizing table row behavior
   * @template T - Type extending IData for the row item
   * @property {function} onClick - Handler called when row is clicked, receives event and row item
   * @property {function} [disabled] - Optional function to determine if row should be disabled
   * @extends {Omit<TableRowProps, 'classe' | 'className' | 'sx' | 'onClick'>}
   */
  rowProps: PropTypes.object,
  /**
   * Sort initial config.
   */
  sortOptions: PropTypes.shape({
    initialOrder: PropTypes.oneOf(['asc', 'desc']),
    initialOrderBy: PropTypes.string
  }),
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * Props passed down to the `TableContainer` component.
   */
  tableContainerProps: PropTypes.object,
  /**
   * Additional items to render before the table.
   */
  tablePreContent: PropTypes.node,
  /**
   * Additional items to render inside the toolbar.
   */
  toolbarItems: PropTypes.node,
  /**
   * To include checkboxes.
   */
  withCheckbox: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /**
   * Include search functionality.
   */
  withSearch: PropTypes.shape({
    placeholder: PropTypes.string,
    query: PropTypes.string.isRequired,
    transformQuery: PropTypes.func,
    withStar: PropTypes.bool
  })
} : void 0;
export default ContainerTable;